import React, { useState, useEffect } from 'react';
import { Compass, Award, Flame } from 'lucide-react';
import axios from 'axios';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BADGE_CATALOG = {
  // Streak Badges
  first_reading: { name: 'Awakening', emoji: '🌅', desc: 'Your first reading', category: 'Journey' },
  first_light: { name: 'First Light', emoji: '✨', desc: '5-day streak', category: 'Streak', requirement: 5 },
  path_walker: { name: 'Path Walker', emoji: '🚶', desc: '10-day streak', category: 'Streak', requirement: 10 },
  cycle_keeper: { name: 'Cycle Keeper', emoji: '🌙', desc: '21-day streak', category: 'Streak', requirement: 21 },
  phoenix_rising: { name: 'Phoenix Rising', emoji: '🔥', desc: '40-day streak', category: 'Streak', requirement: 40 },
  pattern_master: { name: 'Pattern Master', emoji: '🌟', desc: '100-day streak', category: 'Streak', requirement: 100 },

  // Contribution Badges
  feather: { name: 'Feather', emoji: '🪶', desc: 'Offered 1 reading', category: 'Contribution', requirement: 1 },
  open_book: { name: 'Open Book', emoji: '📖', desc: 'Offered 10 readings', category: 'Contribution', requirement: 10 },
  torch: { name: 'Torch', emoji: '🕯️', desc: 'Offered 25 readings', category: 'Contribution', requirement: 25 },
  tree: { name: 'Tree', emoji: '🌳', desc: 'Offered 50 readings', category: 'Contribution', requirement: 50 },

  // Pattern Discovery Badges
  phoenix_pattern: { name: 'Phoenix', emoji: '🔥', desc: 'Explored rebirth', category: 'Pattern' },
  eclipse_pattern: { name: 'Eclipse', emoji: '🌑', desc: 'Explored shadow', category: 'Pattern' },
  rings_pattern: { name: 'Union', emoji: '💍', desc: 'Explored union', category: 'Pattern' },
  leaf_pattern: { name: 'Letting Go', emoji: '🍂', desc: 'Explored release', category: 'Pattern' },
  hourglass_pattern: { name: 'Patience', emoji: '⏳', desc: 'Explored patience', category: 'Pattern' },
  candle_pattern: { name: 'Devotion', emoji: '🕯️', desc: 'Explored devotion', category: 'Pattern' },

  // Legacy badges (keep for existing users)
  '10_saved': { name: 'Collector', emoji: '💫', desc: '10 saved readings', category: 'Legacy' }
};

export default function WisdomJourney() {
  const [badges, setBadges] = useState([]);
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0 });
  const [stats, setStats] = useState({ total_readings: 0, total_offered: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadJourneyData();
  }, []);

  const loadJourneyData = async () => {
    try {
      const [badgesResp, streakResp, historyResp, offeredResp] = await Promise.all([
        axios.get(`${API}/badges`),
        axios.get(`${API}/streak`),
        axios.get(`${API}/devotionals/history?limit=1000`),
        axios.get(`${API}/collective-library`)
      ]);

      setBadges(badgesResp.data);
      setStreak(streakResp.data);

      // Calculate stats
      const userOffered = historyResp.data.filter(d => d.offered_to_collective).length;
      setStats({
        total_readings: historyResp.data.length,
        total_offered: userOffered
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const earnedBadgeTypes = badges.map(b => b.badge_type);

  const categories = {
    'Journey': Object.entries(BADGE_CATALOG).filter(([_, b]) => b.category === 'Journey'),
    'Streak': Object.entries(BADGE_CATALOG).filter(([_, b]) => b.category === 'Streak'),
    'Contribution': Object.entries(BADGE_CATALOG).filter(([_, b]) => b.category === 'Contribution'),
    'Pattern': Object.entries(BADGE_CATALOG).filter(([_, b]) => b.category === 'Pattern')
  };

  const getProgress = (badgeType, badgeInfo) => {
    if (earnedBadgeTypes.includes(badgeType)) return 100;

    if (badgeInfo.category === 'Streak' && badgeInfo.requirement) {
      return Math.min(100, (streak.current_streak / badgeInfo.requirement) * 100);
    }

    if (badgeInfo.category === 'Contribution' && badgeInfo.requirement) {
      return Math.min(100, (stats.total_offered / badgeInfo.requirement) * 100);
    }

    return 0;
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <div className="cosmic-loader"></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="fade-in max-w-[1100px] mx-auto pb-24">
        {/* Header */}
        <div className="text-center mb-12">
          <Compass size={64} className="text-gold mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase">Your Wisdom Journey</h1>
          <p className="text-gold italic mt-2 text-lg">
            The path reveals itself through devotion
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-12">
          <div className="mystical-card p-6 text-center">
            <Flame size={32} className="text-gold mx-auto mb-2" />
            <h3 className="text-3xl font-bold my-2">{streak.current_streak}</h3>
            <p className="text-sm text-gray-400 uppercase tracking-widest font-bold">Day Streak</p>
            <p className="text-xs text-gray-600 mt-1 uppercase tracking-tighter">Longest: {streak.longest_streak}</p>
          </div>

          <div className="mystical-card p-6 text-center">
            <Award size={32} className="text-gold mx-auto mb-2" />
            <h3 className="text-3xl font-bold my-2">{earnedBadgeTypes.length}</h3>
            <p className="text-sm text-gray-400 uppercase tracking-widest font-bold">Seals Earned</p>
          </div>

          <div className="mystical-card p-6 text-center">
            <Compass size={32} className="text-gold mx-auto mb-2" />
            <h3 className="text-3xl font-bold my-2">{stats.total_readings}</h3>
            <p className="text-sm text-gray-400 uppercase tracking-widest font-bold">Readings</p>
          </div>
        </div>

        {/* Badge Categories */}
        {Object.entries(categories).map(([categoryName, categoryBadges]) => (
          <div key={categoryName} className="mystical-card p-8 mb-8">
            <h2 className="text-2xl font-bold mb-8 uppercase tracking-widest border-b border-gold/20 pb-4">{categoryName}</h2>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {categoryBadges.map(([badgeType, badgeInfo]) => {
                const earned = earnedBadgeTypes.includes(badgeType);
                const progress = getProgress(badgeType, badgeInfo);

                return (
                  <div
                    key={badgeType}
                    data-testid={`badge-${badgeType}`}
                    className={`text-center p-4 rounded-lg border transition-all duration-500 ${earned
                      ? 'bg-gold/10 border-gold/50 opacity-100'
                      : 'bg-black/30 border-gold/20 opacity-60 grayscale hover:grayscale-0 hover:opacity-80'
                      }`}
                  >
                    <div className={`text-5xl mb-2 transition-transform duration-500 ${earned ? 'scale-110' : 'scale-90 opacity-40'}`}>
                      {badgeInfo.emoji}
                    </div>

                    <h3 className={`text-sm font-bold mb-1 uppercase tracking-tighter ${earned ? 'text-gold' : 'text-gray-500'}`}>
                      {badgeInfo.name}
                    </h3>

                    <p className="text-[10px] text-gray-500 mb-3 leading-tight uppercase font-medium">
                      {badgeInfo.desc}
                    </p>

                    {/* Progress Bar */}
                    {!earned && progress > 0 && (
                      <div className="w-full h-1 bg-gold/10 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-gold to-yellow-400 transition-all duration-500"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    )}

                    {earned && (
                      <div className="text-[10px] text-gold italic mt-2 uppercase font-bold tracking-widest">
                        ✓ Earned
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <Footer />
    </div>
  );
}
